import doRequest from '../services/request';
import {
  ChildTenant,
  InternalFilter,
  InternalPage,
  Kpis,
  Page,
  RequestField,
  RequestType,
  TicketDetail,
  TicketSummary,
} from '../types/responses/Jira';

/**
 * Fala com /api/v1/jira/* pelo mesmo caminho do restante do dashboard (services/request.js:
 * Api-Key, Platform, Authorization). O backend decide tenant e usuário pelo JWT: nada aqui envia
 * orgId, e-mail ou chave. Rejeita com `{ response: { status } }` em erro HTTP, como hooks/schedules.ts.
 */
const call = async <T>(method: string, url: string, body?: unknown): Promise<T> => {
  const raw: any = await doRequest(url, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!raw.ok) {
    const error: any = new Error(`Jira request failed (${raw.status})`);
    error.response = { status: raw.status };
    throw error;
  }
  return (await raw.json()) as T;
};

const qs = (params: Record<string, string | number | undefined>) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');

// ── cliente ──
export const setupJira = () => call<{ status: string }>('POST', 'jira/setup', {});
export const getRequestTypes = () => call<RequestType[]>('GET', 'jira/request-types');
export const getRequestTypeFields = (id: string) =>
  call<RequestField[]>('GET', `jira/request-types/${encodeURIComponent(id)}/fields`);
export const getChildTenants = () => call<ChildTenant[]>('GET', 'jira/child-tenants');

export const getTickets = (p: { status?: string; childTenantPk?: number; pageToken?: string }) =>
  call<InternalPage<TicketSummary>>(
    'GET',
    `jira/tickets?${qs({ status: p.status, childTenantPk: p.childTenantPk, pageToken: p.pageToken })}`,
  );

export const getTicket = (key: string) => call<TicketDetail>('GET', `jira/tickets/${encodeURIComponent(key)}`);

export const createTicket = (payload: {
  requestTypeId: string;
  fields: Record<string, string>;
  temporaryAttachmentIds: string[];
  onBehalfOfTenantPk?: number;
}) => call<TicketSummary>('POST', 'jira/tickets', payload);

export const sendComment = (key: string, body: string) =>
  call<{ status: string }>('POST', `jira/tickets/${encodeURIComponent(key)}/comments`, { body });

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });

export const uploadTempAttachment = async (file: File): Promise<string> => {
  const contentBase64 = await readAsBase64(file);
  const r = await call<{ temporaryAttachmentId: string }>('POST', 'jira/attachments/temp', {
    fileName: file.name,
    contentBase64,
  });
  return r.temporaryAttachmentId;
};

// ── interno (Move) ──
export const getInternalTickets = (f: InternalFilter) =>
  call<InternalPage<TicketSummary>>('GET', `internal/jira/tickets?${qs({ ...f })}`).then((r) => ({
    ...r,
    page: r.page ?? 1,
    nextPageToken: r.nextPageToken ?? null,
  })) as Promise<Page<TicketSummary> & { nextPageToken: string | null }>;
export const getInternalTicket = (key: string) =>
  call<TicketDetail>('GET', `internal/jira/tickets/${encodeURIComponent(key)}`);
export const sendInternalComment = (key: string, body: string, isPublic: boolean) =>
  call<{ status: string }>('POST', `internal/jira/tickets/${encodeURIComponent(key)}/comments`, {
    body,
    public: isPublic === true,
  });
export const getTransitions = (key: string) =>
  call<{ values: { id: string; name: string }[] }>(
    'GET',
    `internal/jira/tickets/${encodeURIComponent(key)}/transitions`,
  );
export const applyTransition = (key: string, transitionId: string) =>
  call<{ status: string }>('POST', `internal/jira/tickets/${encodeURIComponent(key)}/transitions`, {
    transitionId,
  });
export const assignTicket = (key: string, accountId: string) =>
  call<{ status: string }>('PUT', `internal/jira/tickets/${encodeURIComponent(key)}/assignee`, { accountId });
export const getKpis = () => call<Kpis>('GET', 'internal/jira/kpis');
export const invalidateJiraCache = () => call<{ status: string }>('DELETE', 'internal/jira/cache');
